// ═══════════════════════════════════════════════════════════════
// useChecklists — the mechanic's reusable inspection checklists.
//
// Stored one doc per checklist at users/{uid}/checklists/{id}, which
// the existing users/{uid}/{document=**} rule already covers — no
// firestore.rules change.
//
// A mechanic who has never opened Settings has no saved checklists,
// so the hook falls back to the shipped default rather than handing
// back an empty list. That keeps "start an inspection" working on a
// brand-new account with zero setup, while still letting anyone who
// wants three different checklists have them.
// ═══════════════════════════════════════════════════════════════
import { useEffect, useState, useMemo } from 'react';
import { doc, onSnapshot, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { checklistsCol } from '../database.js';
import { normalizeChecklist, cloneDefaultChecklist } from './inspection-template.js';

export function useChecklists() {
  const [saved, setSaved] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      checklistsCol(),
      (snap) => {
        setSaved(
          snap.docs
            .map((d) => normalizeChecklist({ ...d.data(), id: d.id }))
            .filter(Boolean)
            // Firestore returns docs in key order; sort by name so the
            // Settings list and the wizard picker agree.
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        setReady(true);
      },
      (err) => {
        console.error('Checklists load failed:', err);
        setReady(true);
      }
    );
    return unsub;
  }, []);

  // What the rest of the app should offer. Never empty.
  const checklists = useMemo(
    () => (saved.length > 0 ? saved : [cloneDefaultChecklist()]),
    [saved]
  );

  // True when we're showing the built-in rather than anything saved —
  // Settings uses this to offer "start from the standard checklist".
  const usingDefault = ready && saved.length === 0;

  async function saveChecklist(checklist) {
    const clean = normalizeChecklist(checklist);
    if (!clean) throw new Error('Checklist is empty.');
    if (clean.sections.length === 0) {
      throw new Error('A checklist needs at least one section with an item in it.');
    }
    const { id, ...rest } = clean;
    await setDoc(
      doc(checklistsCol(), id),
      { ...rest, updatedAt: serverTimestamp() },
      { merge: true }
    );
    return id;
  }

  async function deleteChecklist(id) {
    await deleteDoc(doc(checklistsCol(), id));
  }

  return { checklists, saved, usingDefault, ready, saveChecklist, deleteChecklist };
}
